import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invitations, snippets, teams } from '../api';
import type { InvitationResponse, SharePermission, TeamRole } from '../api';
import { snippetKey, tagsKey } from './queries';
import { sharesKey, teamsKey } from './teamQueries';

/** Every snippet list, whatever it was filtered by. */
const allSnippetLists = ['snippets'] as const;

export const myInvitationsKey = ['invitations'] as const;
export const teamInvitationsKey = (teamId: string) => ['team-invitations', teamId] as const;
export const snippetInvitationsKey = (snippetId: string) =>
  ['snippet-invitations', snippetId] as const;

/**
 * Invitations addressed to the signed-in user.
 *
 * Enabled unconditionally, including for an unconfirmed address: the API answers with an empty
 * list rather than an error there, so this stays a quiet background query on every page rather
 * than something that has to be guarded at each call site.
 */
export function useMyInvitations() {
  return useQuery({
    queryKey: myInvitationsKey,
    queryFn: async (): Promise<InvitationResponse[]> => {
      const { data, error } = await invitations.mine();
      if (error) {
        throw error;
      }
      return data ?? [];
    },
  });
}

/**
 * Everything an accepted invitation could have changed.
 *
 * A team invitation opens a whole library — new snippets, new tags, a new entry in the switcher —
 * and a snippet invitation adds one snippet to "shared with me". Invalidating all of it is cheaper
 * than working out which, and getting it wrong shows up as a library that appears empty until the
 * page is reloaded.
 */
function invalidateEverythingAnAcceptTouches(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: myInvitationsKey });
  void queryClient.invalidateQueries({ queryKey: teamsKey });
  void queryClient.invalidateQueries({ queryKey: allSnippetLists });
  void queryClient.invalidateQueries({ queryKey: tagsKey });
}

export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await invitations.accept({ path: { id } });
      if (error) {
        throw error;
      }
      return data!;
    },
    onSuccess: () => invalidateEverythingAnAcceptTouches(queryClient),
  });
}

export function useDeclineInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await invitations.decline({ path: { id } });
      if (error) {
        throw error;
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: myInvitationsKey }),
  });
}

export function useTeamInvitations(teamId: string | null) {
  return useQuery({
    queryKey: teamInvitationsKey(teamId ?? ''),
    enabled: teamId !== null,
    queryFn: async (): Promise<InvitationResponse[]> => {
      const { data, error } = await teams.listInvitations({ path: { id: teamId! } });
      if (error) {
        throw error;
      }
      return data ?? [];
    },
  });
}

export function useInviteMember(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { email: string; role: TeamRole }) => {
      const { data, error } = await teams.invite({ path: { id: teamId }, body: input });
      if (error) {
        throw error;
      }
      return data!;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: teamInvitationsKey(teamId) }),
  });
}

export function useRevokeTeamInvitation(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await teams.revokeInvitation({
        path: { id: teamId, invitationId },
      });
      if (error) {
        throw error;
      }
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: teamInvitationsKey(teamId) }),
  });
}

export function useResendTeamInvitation(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await teams.resendInvitation({
        path: { id: teamId, invitationId },
      });
      if (error) {
        throw error;
      }
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: teamInvitationsKey(teamId) }),
  });
}

export function useSnippetInvitations(snippetId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: snippetInvitationsKey(snippetId ?? ''),
    // Listing them needs Manage, so asking without it would be a guaranteed 404.
    enabled: snippetId !== null && enabled,
    queryFn: async (): Promise<InvitationResponse[]> => {
      const { data, error } = await snippets.listInvitations({ path: { id: snippetId! } });
      if (error) {
        throw error;
      }
      return data ?? [];
    },
  });
}

/**
 * Offers a snippet to an address.
 *
 * Answers 202 with an invitation for somebody new, or 200 with the share list when the recipient
 * already accepted one and this only changes what it grants. Both invalidate both lists rather
 * than the caller having to tell which happened.
 */
export function useShareSnippetByInvitation(snippetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { email: string; permission: SharePermission }) => {
      const { error } = await snippets.share({ path: { id: snippetId }, body: input });
      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: snippetInvitationsKey(snippetId) });
      void queryClient.invalidateQueries({ queryKey: sharesKey(snippetId) });
    },
  });
}

export function useRevokeSnippetInvitation(snippetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await snippets.revokeInvitation({
        path: { id: snippetId, invitationId },
      });
      if (error) {
        throw error;
      }
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: snippetInvitationsKey(snippetId) }),
  });
}

export function useResendSnippetInvitation(snippetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await snippets.resendInvitation({
        path: { id: snippetId, invitationId },
      });
      if (error) {
        throw error;
      }
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: snippetInvitationsKey(snippetId) }),
  });
}

/** Gives up a share of somebody else's snippet — the counterpart of leaving a team. */
export function useLeaveShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (snippetId: string) => {
      const { error } = await snippets.leaveShare({ path: { id: snippetId } });
      if (error) {
        throw error;
      }
    },
    onSuccess: (_, snippetId) => {
      void queryClient.invalidateQueries({ queryKey: allSnippetLists });

      // Removed rather than invalidated: refetching a snippet the caller has just given up access
      // to would answer 404 and surface as an error on the way out.
      queryClient.removeQueries({ queryKey: snippetKey(snippetId) });
    },
  });
}
